import { Box, Container, Tab, Tabs, Collapse, IconButton } from '@mui/material';
import { CommunityThemeProvider } from '../ThemeProvider';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { TabContext, TabPanel } from '@mui/lab';
import { useAuth } from '../auth/Auth';
import { useApi } from '../api/Api';
import { useRequest } from '../api/Request';
import { User, UserSummary } from '../database/user';
import { useCache } from '../api/cache/Cache';
import { PostCategory } from '../database/community';
import GeneralizedPostTab from './GeneralizedPostTab';
import cassetteTapesImage from '../assets/images/Abstract_With_Tapes.jpg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useLocalStorage } from 'usehooks-ts';
import { useSearchParams } from 'react-router-dom';

const validTabs = new Set<string>(Object.values(PostCategory));

const BehindTheScenesPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const linkedPostId = searchParams.get('post') || undefined;
    const mainTab =
        tabParam && validTabs.has(tabParam)
            ? (tabParam as PostCategory)
            : PostCategory.COMMUNITY;

    const setMainTab = (tab: PostCategory) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('tab', tab);
                next.delete('post');
                return next;
            },
            { replace: true },
        );
    };

    const clearLinkedPost = () => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('post');
                return next;
            },
            { replace: true },
        );
    };
    const [bioExpanded, setBioExpanded] = useLocalStorage('bts-bio-expanded', true);
    const [detailsExpanded, setDetailsExpanded] = useLocalStorage(
        'bts-details-expanded',
        false,
    );
    const auth = useAuth();
    const user = auth.user;
    const api = useApi();
    const [artist, setArtist] = useState<User>();
    const getArtistUserRequest = useRequest<User>();
    const cache = useCache();

    useEffect(() => {
        if (!getArtistUserRequest.isSent()) {
            getArtistUserRequest.onStart();
            api.getUserPublic('artistUsername')
                .then((resp) => {
                    setArtist(resp.data);
                    const artistUserSummary: UserSummary = {
                        username: resp.data.username,
                        displayName: resp.data.displayName,
                        isArtist: resp.data.isArtist,
                        subscriptionStatus: resp.data.subscriptionStatus,
                        subscriptionOverride: resp.data.subscriptionOverride,
                        quote: resp.data.quote,
                    };
                    cache.userSummaries.put(artistUserSummary);
                    cache.userSummaries.markFetched(resp.data.username);
                    getArtistUserRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('Error fetching artist data:', err);
                    getArtistUserRequest.onFailure(err);
                });
        }
    }, [getArtistUserRequest, api, cache.userSummaries]);

    return (
        <Container maxWidth='xl' sx={{ mb: '100px' }}>
            <Box sx={{ width: '100%', typography: 'body1' }}>
                {/* Hero Section with Image and Bio Text */}
                {artist && (
                    <Box sx={{ mt: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                mb: 1,
                                cursor: 'pointer',
                            }}
                            onClick={() => setBioExpanded(!bioExpanded)}
                        >
                            <Box sx={{ fontSize: '0.9rem', mr: 1 }}>
                                {bioExpanded ? 'Hide' : 'Show'}
                            </Box>
                            <IconButton size='small' color='inherit'>
                                {bioExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Box>
                        <Collapse in={bioExpanded}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3,
                                    flexDirection: { xs: 'column', md: 'row' },
                                }}
                            >
                                <motion.img
                                    src={cassetteTapesImage}
                                    alt='Behind the Scenes'
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                    style={{
                                        width: '100%',
                                        maxWidth: '300px',
                                        height: 'auto',
                                        borderRadius: '8px',
                                        objectFit: 'cover',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                    }}
                                />
                                <Box
                                    sx={{
                                        flex: 1,
                                        color: 'text.primary',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontSize: '0.875rem',
                                            lineHeight: 1.5,
                                            fontWeight: 'bold',
                                            mb: 2,
                                        }}
                                    >
                                        Thank you for joining ATWW! This community is
                                        where I will devote most my efforts for the
                                        foreseeable future. In an era where every platform
                                        is governed by greed, attention over connection,
                                        we have built this to combat the alienation so
                                        many now feel in the online spaces in which we
                                        used to enthusiastically congregate. While there
                                        are platforms who offer this type of service to
                                        artists, I wanted to make one that is our own
                                        where all of you can help me shape what it
                                        becomes.
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            mt: 1,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() =>
                                            setDetailsExpanded(!detailsExpanded)
                                        }
                                    >
                                        <Box sx={{ fontSize: '0.9rem', mr: 1 }}>
                                            {detailsExpanded
                                                ? 'Hide Details'
                                                : 'More Details'}
                                        </Box>
                                        <IconButton size='small' color='inherit'>
                                            {detailsExpanded ? (
                                                <ExpandLessIcon />
                                            ) : (
                                                <ExpandMoreIcon />
                                            )}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={detailsExpanded}>
                                        <Box
                                            component='ul'
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.6,
                                                pl: 3,
                                                m: 0,
                                                listStyleType: 'disc',
                                            }}
                                        >
                                            <li>
                                                All new music will arrive here first, I'll
                                                be debuting demos that may or may not get
                                                released.
                                            </li>
                                            <li>
                                                Behind the scenes looks at upcoming Merch
                                                and videos
                                            </li>
                                            <li>First access to Tour News and tickets</li>
                                            <li>
                                                Gain access to EnterStellar Spacebar, our
                                                hidden forum and community hub, where we
                                                connect beyond the algorithm, a community
                                                driven space that calls back to the golden
                                                age of the internet. You'll have to
                                                explore to find it... but I'll leave you
                                                with a clue: "What's in the name?"
                                            </li>
                                            <li>
                                                The opportunity for you to challenge me to
                                                record my own song over another songs
                                                instrumental which the community chooses
                                                monthly
                                            </li>
                                            <li>
                                                This will allow us the funding to continue
                                                to build out the future of our community,
                                                The Moon Five Two Virtual Store which I've
                                                teased construction of on the Moon Five
                                                Two instagram, the same place the Moon
                                                Droid RPG will live.
                                            </li>
                                            <li>
                                                Discussions and conversations about the
                                                current state of the world and music, an
                                                opportunity to engage in critical thought
                                                which the internet seems to have become
                                                allergic to
                                            </li>
                                            <li>
                                                I'll be debuting my podcast "Diabolical
                                                Light" in the coming weeks and intend to
                                                make it a weekly upload on this sight. The
                                                name spurs from it's aim to discuss
                                                dangerously good ideas.
                                            </li>
                                            <li>
                                                This community will allow me to create
                                                more freely and with less pressure. From
                                                2019 to now, like all things, this has
                                                become a much harder life path to pursue.
                                                Every bit of support here means so much
                                                more than you know, I've had quite the
                                                journey the last five years and I can't
                                                wait to tell you more about it but I don't
                                                want to just survive this world together,
                                                I want us to thrive.
                                            </li>
                                            <li>
                                                This community will give me a chance to
                                                break away from an industry whose
                                                platforms now time and time again signal
                                                that they are engaging in corruption. To a
                                                level where I feel dirty having my
                                                business and my art tied to them, from the
                                                president of *the green music logo*
                                                investing hundreds of millions in overseas
                                                AI drone military defense??? The same year
                                                they stopped paying out artists with less
                                                than 1,000 streams and pocketed all those
                                                instances of profit? Whose money really
                                                went into that investment? smh anyway, I'm
                                                rambling and thats a topic for Diabolical
                                                Light.
                                            </li>
                                            <li>
                                                This life path and the freedom to create
                                                and express myself how I wanted to was
                                                always a massive draw for me. As the years
                                                go by I realize I am not free as I thought
                                                I was. I am still at the mercy of these
                                                platforms which I utilize to share my
                                                music with the world, subject to the whims
                                                of their now completely robotic sorting
                                                systems, algorithms and "optimizations"
                                                but those optimizations have never seemed
                                                to be for the benefit of people like me,
                                                the creator of the content which they bank
                                                off, its always been in the interest of
                                                them and their shareholders and I"d like
                                                to take back control of my art and
                                                livelihood. This is what I live for, it's
                                                what I know I was born to do and with this
                                                platform I am allowing every one of you to
                                                invest in what I know I'm still yet to
                                                become.
                                            </li>
                                            <li>
                                                This community allows me direct access to
                                                all of you, no blind hoping that the
                                                algorithm gets my work onto your feed.
                                                You'll know about it all on a first to
                                                know basis.
                                            </li>
                                            <li>
                                                Every streaming model seems to keep
                                                raising their prices monthly, here at ATWW
                                                I am making a process (mechanically built
                                                into the site) to never raise your
                                                subscription here. What you sign up at is
                                                what you'll pay forever even if I raise it
                                                at some point, you will be locked in at
                                                your original price. Gotta show love to
                                                OGs
                                            </li>
                                            <li>
                                                If you own a Munich Moment Card, reach out
                                                and let us know your email. All Munich
                                                Moment Card owners get free access here
                                                for life.
                                            </li>
                                        </Box>
                                    </Collapse>
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                )}
                <CommunityThemeProvider>
                    <Box
                        sx={(theme) => ({
                            position: 'relative',
                            borderRadius: '20px 20px 0 0',
                            mt: 3,
                            pt: '2px',
                            bgcolor: 'background.default',
                            minHeight: '60vh',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '8%',
                                right: '8%',
                                height: '2px',
                                background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, transparent)`,
                                borderRadius: '0 0 2px 2px',
                            },
                        })}
                    >
                        <TabContext value={mainTab}>
                            <Box
                                sx={{
                                    width: '100%',
                                    typography: 'body1',
                                    mt: 5,
                                    justifyContent: 'center',
                                    display: 'flex',
                                }}
                            >
                                <Tabs
                                    value={mainTab}
                                    textColor='inherit'
                                    onChange={(_, val) => setMainTab(val)}
                                    variant='scrollable'
                                >
                                    <Tab label='Community' value={PostCategory.COMMUNITY} />
                                    <Tab label='Sound Vault' value={PostCategory.MUSIC} />
                                    <Tab label='Behind the Lens' value={PostCategory.VIDEO} />
                                    <Tab label='Snapshots' value={PostCategory.PHOTO} />
                                </Tabs>
                            </Box>
                            <TabPanel value={PostCategory.COMMUNITY} sx={{ p: 0 }}>
                                {artist && (
                                    <GeneralizedPostTab
                                        artist={artist}
                                        user={user}
                                        requiresSubscription={false}
                                        artistPostsOnly={false}
                                        postCategory={PostCategory.COMMUNITY}
                                        linkedPostId={
                                            mainTab === PostCategory.COMMUNITY
                                                ? linkedPostId
                                                : undefined
                                        }
                                        onClearLinkedPost={clearLinkedPost}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={PostCategory.MUSIC} sx={{ p: 0 }}>
                                {artist && (
                                    <GeneralizedPostTab
                                        artist={artist}
                                        user={user}
                                        requiresSubscription={true}
                                        artistPostsOnly={true}
                                        postCategory={PostCategory.MUSIC}
                                        linkedPostId={
                                            mainTab === PostCategory.MUSIC
                                                ? linkedPostId
                                                : undefined
                                        }
                                        onClearLinkedPost={clearLinkedPost}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={PostCategory.VIDEO} sx={{ p: 0 }}>
                                {artist && (
                                    <GeneralizedPostTab
                                        artist={artist}
                                        user={user}
                                        requiresSubscription={true}
                                        artistPostsOnly={true}
                                        postCategory={PostCategory.VIDEO}
                                        linkedPostId={
                                            mainTab === PostCategory.VIDEO
                                                ? linkedPostId
                                                : undefined
                                        }
                                        onClearLinkedPost={clearLinkedPost}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={PostCategory.PHOTO} sx={{ p: 0 }}>
                                {artist && (
                                    <GeneralizedPostTab
                                        artist={artist}
                                        user={user}
                                        requiresSubscription={true}
                                        artistPostsOnly={true}
                                        postCategory={PostCategory.PHOTO}
                                        linkedPostId={
                                            mainTab === PostCategory.PHOTO
                                                ? linkedPostId
                                                : undefined
                                        }
                                        onClearLinkedPost={clearLinkedPost}
                                    />
                                )}
                            </TabPanel>
                        </TabContext>
                    </Box>
                </CommunityThemeProvider>
            </Box>
        </Container>
    );
};

export default BehindTheScenesPage;
